import React, { useMemo } from 'react';
import { StyledFlex } from '@goodfynd/styles';
import { Icon } from '@goodfynd/ui';
import { Text } from '@goodfynd/ui';
import { getAmountDisplay, getDisplayColor, numberUtil } from '@goodfynd/utils';
import { stringUtil } from '@goodfynd/utils';
import { renderNumber } from '@utils/number-util';

import config from '../../../../config';
import { useOrdersOverview } from '../../orders-overview-context/OrdersOverviewProvider';
import { OverviewCard } from '../../overview-card';

import { CustomersOverviewLoading } from './CustomersOverviewLoading';
import { StyledChart, StyledColumnLeft, StyledColumnRight } from './styles';
import type { CustomersOverviewProps } from './types';
export default function CustomersOverview({
  ...props
}: CustomersOverviewProps) {
  const {
    customerOverviewIsFetching,
    customers: {
      newCustomers = 0,
      avgTotal = 0,
      avgItemCount = 0,
      repeatCustomers = 0,
      avgRepeatTotal = 0,
      avgRepeatItemCount = 0,
    } = {},
    customersLastPeriod: {
      newCustomers: newCustomersLastPeriod = 0,
      repeatCustomers: repeatCustomersLastPeriod = 0,
    } = {},
    dateEndCompare,
    dateStartCompare,
  } = useOrdersOverview();

  const percentage = useMemo<number>(
    () =>
      numberUtil.calculatePercentChange(newCustomersLastPeriod, newCustomers),
    [newCustomers, newCustomersLastPeriod]
  );

  const percentageRepeat = useMemo<number>(
    () =>
      numberUtil.calculatePercentChange(
        repeatCustomersLastPeriod,
        repeatCustomers
      ),
    [repeatCustomers, repeatCustomersLastPeriod]
  );

  return (
    <OverviewCard {...props} id="overview-customers" title="Customers">
      {customerOverviewIsFetching ? (
        <CustomersOverviewLoading />
      ) : (
        <StyledChart>
          <StyledFlex>
            <StyledColumnLeft>
              <Text as="caption" display="bold">
                New customers
              </Text>

              <Text
                as="heading2"
                display="bold"
                title={renderNumber(newCustomers)}
              >
                {getAmountDisplay(newCustomers, { mantissa: 0 })}
              </Text>
              <Text
                as="caption"
                tag="span"
                style={{
                  alignItems: 'center',
                  color: `$${getDisplayColor(percentage)}`,
                  display: 'flex',
                }}
              >
                <Icon
                  css={{ marginBottom: 2 }}
                  name={percentage >= 0 ? 'arrow_up' : 'arrow_down'}
                  size={16}
                />
                {percentage >= 0 ? config.unicode.plus : config.unicode.minus}
                {getAmountDisplay(Math.round(Math.abs(percentage)), {
                  mantissa: 0,
                })}
                %&nbsp;vs&nbsp;
                {stringUtil
                  .getLastPeriodDisplay(dateStartCompare, dateEndCompare)
                  .toLowerCase()}
              </Text>

              <Text as="heading4" style={{ fontWeight: 800, marginTop: 24 }}>
                {'$' +
                  getAmountDisplay(avgTotal, {
                    mantissa: 0,
                  })}
              </Text>
              <Text as="caption" style={{ color: '$Neutral13' }}>
                Avg. order amount
              </Text>

              <Text as="heading4" style={{ fontWeight: 800, marginTop: 24 }}>
                {getAmountDisplay(avgItemCount, {
                  mantissa: 1,
                })}
              </Text>

              <Text as="caption">Avg. items per order</Text>
            </StyledColumnLeft>
            <StyledColumnRight>
              <Text as="caption" display="bold">
                Repeat customers
              </Text>

              <Text
                as="heading2"
                color="TextIconSecondary"
                display="bold"
                title={renderNumber(repeatCustomers)}
              >
                {getAmountDisplay(repeatCustomers, { mantissa: 0 })}
              </Text>

              <Text
                as="caption"
                tag="span"
                style={{
                  alignItems: 'center',
                  color: `$${getDisplayColor(percentageRepeat)}`,
                  display: 'flex',
                }}
              >
                <Icon
                  css={{ marginBottom: 2 }}
                  name={percentageRepeat >= 0 ? 'arrow_up' : 'arrow_down'}
                  size={16}
                />
                {percentageRepeat >= 0
                  ? config.unicode.plus
                  : config.unicode.minus}
                {getAmountDisplay(Math.round(Math.abs(percentageRepeat)), {
                  mantissa: 0,
                })}
                %&nbsp;vs&nbsp;
                {stringUtil
                  .getLastPeriodDisplay(dateStartCompare, dateEndCompare)
                  .toLowerCase()}
              </Text>

              <Text
                as="heading4"
                color="TextIconSecondary"
                display="bold"
                style={{ marginTop: 24 }}
              >
                {'$' + getAmountDisplay(avgRepeatTotal, { mantissa: 0 })}
              </Text>

              <Text as="caption">Avg. order amount</Text>

              <Text
                as="heading4"
                color="TextIconSecondary"
                display="bold"
                style={{ marginTop: 24 }}
              >
                {getAmountDisplay(avgRepeatItemCount, {
                  mantissa: 1,
                })}
              </Text>

              <Text as="caption">Avg. items per order</Text>
            </StyledColumnRight>
          </StyledFlex>
        </StyledChart>
      )}
    </OverviewCard>
  );
}
